import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { MutationController } from '@lit-labs/observers/mutation-controller.js';
import { useEffect } from 'haunted';
import CSS_NAMED_COLORS from 'colors-named'
import chroma from 'chroma-js'
import { LitHauntedElement } from '../mixins/lit-haunted-element';
import { ResolveCSSColor } from '../functions/resolve_css_color';

const FONT_SIZE_EXPRESSION = css`var(--size)*0.66`;
const EXCLUDED_COLORS: string[] = ['blue']
export const NAMED_COLORS: string[] = Array.from(new Set([
  'dodgerblue', 'green', '#D83E5D', 'darkorange', '#EABB00', 'blueviolet', // base
  'mediumblue', 'olivedrab', '#AF1131', 'orangered', 'peru', 'indigo', // darker
  'cornflowerblue', 'mediumseagreen', 'salmon', 'orange', 'gold', 'mediumpurple', // lighter
  'deepskyblue', 'limegreen', 'hotpink', 'coral', '#FFD335', 'violet', // off saturated
  'steelblue', 'teal', 'red', 'lightsalmon', 'tan', 'rebeccapurple', // off desaturated
  ...CSS_NAMED_COLORS]))
  .filter(value => {
    const color = chroma(value)
    const [_, saturation, lightness] = color.hsl()
    const luminance = color.luminance()
    return lightness < 0.75 && luminance < 0.7 && saturation > 0.1;
  })
  .filter(v => !EXCLUDED_COLORS.includes(v))

@customElement('ep-number-badge')
export class EPNumberBadge extends LitHauntedElement {
  // Adjusts font-size whenever the slotted element mutates
  // @ts-ignore we
  private _observer = new MutationController(this, { 
    target: this,
    callback: () => this._adjustFontSize(),
    config: { attributes: false, characterData: true, subtree: true, }
  })
  // Estimates the number of digits used to makeup the number
  get digits(): number {
    return this.textContent?.trim().length ?? 1;
  }

  @property({ type: Boolean, reflect: true })
  circle: boolean = false;

  @property({ type: Number, attribute: 'named-color-index' })
  namedColorIndex?: number;

  @property({ type: Number, })
  scale: number = 1.0;

  // (Attempts to) adjust the font-size so that the number fits cleanly inside the badge
  private _adjustFontSize() {
    const digits = this.digits;
    const coefficient = 1 - (0.1 * digits) + (digits > 2 ? 0 : 0.2)
    //console.log('digits?', digits, 'coefficient?', coefficient);
    this.style.fontSize = `calc(${FONT_SIZE_EXPRESSION} * ${Math.max(coefficient, 0.0001)} * ${this.scale})`;
  }

  // Adjusts font-size whenever the slotted element is replaced
  render() {
    useEffect(() => {
      if (this.namedColorIndex !== undefined) {
        const i = this.namedColorIndex % NAMED_COLORS.length;
        const namedColor = NAMED_COLORS[i] ?? NAMED_COLORS[0];
        const rgbExp = ResolveCSSColor(namedColor) // `rgb(0, 255, 255)`
        const rgb = rgbExp.substring(4, rgbExp.indexOf(')')) // `0, 255, 255`
        this.style.setProperty('--accent-rgb', rgb);
      }
    }, [this.namedColorIndex]);
    useEffect(() => this._adjustFontSize(), [this.scale])

    return html`
      <slot @slotchange=${this._adjustFontSize}></slot>
    `
  }
  static styles = css`
    :host {
      --size: 24px;
      --accent-rgb: 25,118,210;
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: var(--size);
      height: var(--size);
      background-color: rgba(var(--accent-rgb),0.25);
      border-radius: 25%;
      line-height: normal;
    }

    :host-context([circle]) {
      border-radius: 50%;
    }

    :host, ::slotted(*) {
      display: inline-flex!important;
      justify-content: center!important;
      align-items: center!important;
      color: rgb(var(--accent-rgb))!important;
      font-family: var(--font-prose);
      font-size: calc(${FONT_SIZE_EXPRESSION});
      font-weight: 500;
      vertical-align: middle;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-number-badge': EPNumberBadge
  }
}
